import { Trans } from "@lingui/react/macro";
import { Filter } from "lucide-react";
import { useState } from "react";

import type { DataTableInstance } from "../table-features";
import type { FilterField } from "./types";

import { cn } from "../../../../../utils/shadcn-ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../base/popover";
import { FilterFieldEditor } from "./filter-field-editor";
import { isFieldActive } from "./tanstack-bridge";

/**
 * Funnel button rendered in a column header that opens the column's filter
 * editor in a popover. Rest/hover opacity comes from `triggerClassName`
 * (SortHeader mirrors its sort-icon ramp); Radix's `data-state` and the
 * `data-active` attribute make it fully opaque while open or filtering.
 */
export function HeaderFilterPopover<T>({
  table,
  field,
  triggerClassName,
}: {
  table: DataTableInstance<T>;
  field: FilterField<T>;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const active = isFieldActive(table, field);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-active={active || undefined}
          // A sortable header sorts on click — don't let the funnel trigger it.
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex bg-transparent border-0 p-0 cursor-pointer",
            active || open ? "text-brand-primary" : "text-muted-foreground",
            triggerClassName,
          )}
        >
          <Filter size={12} fill={active ? "currentColor" : "none"} />
          {/* The label is a node (usually the column's `<Trans>` header), so the
              accessible name comes from hidden content, not an aria-label. */}
          <span className="sr-only">
            <Trans>{field.label} filter</Trans>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-72">
        <div
          className="pt-1.5"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <FilterFieldEditor
            table={table}
            field={field}
            autoFocusText
            onSubmit={() => setOpen(false)}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
